/**
 * Calculate maximum allowed mismatches based on query length and identity percentage
 * Formula: Query length * (100 - %identity) / 100
 */
export const calculateMaxMismatches = (querySequence, identityPercentage) => {
    if (!querySequence || querySequence.trim() === '') return 0;
    
    // Parse the query sequence to get actual sequence length (excluding FASTA headers and whitespace)
    let lines = querySequence.split(/[\r\n]+/);
    let sequenceOnly = '';
    
    for (let i = 0; i < lines.length; i++) {
        // Skip FASTA headers and empty lines
        if (!lines[i].startsWith('>') && !lines[i].startsWith('@') && lines[i].trim() !== '') {
            sequenceOnly += lines[i].replace(/[\s\d]/g, ''); // Remove whitespace and digits
        }
    }
    
    const queryLength = sequenceOnly.length;
    const identity = parseFloat(identityPercentage) || 0;
    
    // Formula: Query length * (100 - %identity) / 100
    const maxMismatches = Math.floor(queryLength * (100 - identity) / 100);
    
    return maxMismatches;
};

/**
 * Get sequence length from query sequence (excluding FASTA headers and whitespace)
 */
export const getSequenceLength = (querySequence) => {
    if (!querySequence || querySequence.trim() === '') return 0;
    
    let lines = querySequence.split(/[\r\n]+/);
    let sequenceOnly = '';
    
    for (let i = 0; i < lines.length; i++) {
        if (!lines[i].startsWith('>') && !lines[i].startsWith('@') && lines[i].trim() !== '') {
            sequenceOnly += lines[i].replace(/[\s\d]/g, '');
        }
    }
    
    return sequenceOnly.length;
};

/**
 * Check if mismatch text should be displayed based on all required conditions
 */
export const shouldDisplayMismatchText = (
    querySequence,
    queryCount,
    searchAlgorithm,
    percentageOver,
    identityPercentage
) => {
    // Check if query has at least 3 residues first
    const sequenceLength = getSequenceLength(querySequence);
    if (sequenceLength < 3) return false;
    
    // Only one query in the box
    if (queryCount !== 1) return false;
    
    // GenePast or gapless selected (kerr or hash)
    if (searchAlgorithm !== 'kerr' && searchAlgorithm !== 'hash') return false;
    
    // "Over the query sequence" is selected (QUERY)
    if (percentageOver !== 'QUERY') return false;
    
    // Check if identity percentage has a valid value
    if (!identityPercentage || identityPercentage === '') return false;
    
    return true;
};

/**
 * Count sequences in real-time from input text
 */
export const countSequences = (text) => {
    let val = 0;
    if (text && text.length > 0 && !text.startsWith('Paste your ')) {
        let lines = text.split(/[\r\n]+/);
        if (/^\s*>\s*\S+/.test(text)) { // FASTA
            for (let i = 0; i < lines.length; i++) {
                if (lines[i].substring(0, 1) === '>' && (i + 1) < lines.length && lines[i + 1].substring(0, 1) !== '>') {
                    val++;
                }
            }
        } else if (/^\s*\@\s*\S+/.test(text)) { // FASTAQ
            val = Math.floor(lines.length / 4);
        } else if (/^[a-zA-Z0-9\.][a-zA-Z0-9\.][ \s\t]/.test(text)) { // EMBL
            for (let i = 0; i < lines.length; i++) {
                if (lines[i].substring(0, 2) === '//') {
                    val++;
                }
            }
        } else {
            // Plain sequence, count as 1
            val = 1;
        }
    }
    return val;
};
